import { useEffect } from 'react'

export interface ResourceHint {
  href: string
  crossOrigin?: boolean
}

/**
 * Inyecta <link rel="preconnect"> y <link rel="dns-prefetch"> para los orígenes
 * de terceros (CDN de imágenes, Google Fonts) para abrir la conexión antes de
 * que el navegador descubra los recursos.
 */
export function useResourceHints(hints: ResourceHint[]) {
  useEffect(() => {
    const created: HTMLLinkElement[] = []

    hints.forEach(({ href, crossOrigin }) => {
      const rels = ['preconnect', 'dns-prefetch']
      rels.forEach((rel) => {
        const selector = `link[rel="${rel}"][href="${href}"]`
        if (document.head.querySelector(selector)) return
        const link = document.createElement('link')
        link.rel = rel
        link.href = href
        if (crossOrigin && rel === 'preconnect') link.crossOrigin = 'anonymous'
        document.head.appendChild(link)
        created.push(link)
      })
    })

    return () => {
      created.forEach((link) => link.remove())
    }
  }, [hints])
}

/**
 * Precarga la imagen principal (LCP) con <link rel="preload" as="image"> y
 * prioridad alta. Nunca debe usarse para imágenes bajo el pliegue.
 */
export function usePreloadImage(src: string) {
  useEffect(() => {
    if (!src) return
    const selector = `link[rel="preload"][as="image"][href="${src}"]`
    if (document.head.querySelector(selector)) return

    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = src
    link.setAttribute('fetchpriority', 'high')
    document.head.appendChild(link)

    return () => {
      link.remove()
    }
  }, [src])
}
